import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShieldAlert, Plus, ArrowRight, Search, Trash2, Eye, CheckCircle2, Loader2, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";
import { DropdownBadge, DropdownSelect } from "../components/shared/DropdownHooks";

const NEXT_STATUS = { reported: "investigating", investigating: "cause_identified", cause_identified: "action_planned", action_planned: "implementing", implementing: "verifying", verifying: "closed" };

export default function CAPPage({ onLogout, userRole, isFeatureEnabled }) {
  const [caps, setCaps] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [detailCap, setDetailCap] = useState(null);
  const [actionForm, setActionForm] = useState({ description: "", assigned_to: "", due_date: "" });
  const [causeForm, setCauseForm] = useState({ apparent_cause: "", root_cause: "", extent_of_condition: "" });
  const [form, setForm] = useState({ title: "", description: "", severity: "medium", category: "process", source: "", affected_area: "" });
  const isAdmin = userRole === "admin";

  const fetchAll = useCallback(async () => {
    try {
      const [cR, sR] = await Promise.allSettled([
        axios.get(`${API}/cap`, { headers: getAuthHeaders() }),
        axios.get(`${API}/cap/stats`, { headers: getAuthHeaders() }),
      ]);
      if (cR.status === "fulfilled") setCaps(cR.value.data || []);
      if (sR.status === "fulfilled") setStats(sR.value.data || {});
    } catch {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreate = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    try {
      await axios.post(`${API}/cap`, form, { headers: getAuthHeaders() });
      toast.success("CAP report created"); setCreateOpen(false);
      setForm({ title: "", description: "", severity: "medium", category: "process", source: "", affected_area: "" });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const advance = async (cid, newStatus) => {
    try {
      await axios.post(`${API}/cap/${cid}/transition`, { status: newStatus }, { headers: getAuthHeaders() });
      toast.success(`CAP → ${newStatus.replace(/_/g, " ")}`);
      fetchAll();
      if (detailCap?.id === cid) refreshDetail(cid);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const refreshDetail = async (cid) => {
    try { const r = await axios.get(`${API}/cap/${cid}`, { headers: getAuthHeaders() }); setDetailCap(r.data); }
    catch {}
  };

  const saveCause = async () => {
    if (!detailCap) return;
    try {
      await axios.put(`${API}/cap/${detailCap.id}/cause-analysis`, causeForm, { headers: getAuthHeaders() });
      toast.success("Cause analysis saved"); refreshDetail(detailCap.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const addAction = async () => {
    if (!detailCap || !actionForm.description.trim()) return;
    try {
      await axios.post(`${API}/cap/${detailCap.id}/actions`, actionForm, { headers: getAuthHeaders() });
      toast.success("Action added"); setActionForm({ description: "", assigned_to: "", due_date: "" }); refreshDetail(detailCap.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const completeAction = async (aid) => {
    try {
      await axios.post(`${API}/cap/${detailCap.id}/actions/${aid}/complete`, {}, { headers: getAuthHeaders() });
      toast.success("Action completed"); refreshDetail(detailCap.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveEffectiveness = async (effective) => {
    try {
      await axios.put(`${API}/cap/${detailCap.id}/effectiveness`, { effective, notes: "" }, { headers: getAuthHeaders() });
      toast.success("Effectiveness review saved"); refreshDetail(detailCap.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteCAP = async (id) => {
    if (!window.confirm("Delete this CAP?")) return;
    try { await axios.delete(`${API}/cap/${id}`, { headers: getAuthHeaders() }); toast.success("Deleted"); fetchAll(); if (detailCap?.id === id) setDetailCap(null); }
    catch { toast.error("Failed"); }
  };

  if (loading) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#2563EB]" size={28} /></div></Layout>;

  const filtered = caps.filter(c => {
    if (statusFilter && c.status !== statusFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return c.title?.toLowerCase().includes(q) || c.cap_number?.toLowerCase().includes(q);
  });

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="cap-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <PageHeader title="Corrective Action Program" subtitle="Condition reports, cause analysis, corrective actions, effectiveness reviews" testId="cap-title" />
          <Button onClick={() => setCreateOpen(true)} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-none text-xs font-bold" data-testid="create-cap-btn"><Plus size={14} className="mr-1" /> New CAP</Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="cap-stats">
          {[["Total", stats.total, "#0F172A"], ["Open", stats.open, "#D97706"], ["Investigating", stats.investigating, "#2563EB"], ["Implementing", stats.implementing, "#0EA5E9"], ["Closed", stats.closed, "#10B981"]].map(([l, v, c]) => (
            <div key={l} className="bg-white border border-[#E2E8F0] px-4 py-3"><p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{l}</p><p className="text-2xl font-heading font-black mt-0.5" style={{ color: c }}>{v || 0}</p></div>
          ))}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search CAPs..." className="w-full pl-9 pr-3 py-2.5 border border-[#E2E8F0] text-xs bg-white focus:border-[#2563EB] focus:outline-none" data-testid="cap-search" />
          </div>
          <DropdownSelect dropdownKey="cap_statuses" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-[#E2E8F0] px-3 py-2.5 text-xs bg-white min-w-[140px]" testId="cap-status-filter" placeholder="All Statuses" />
        </div>

        <div className="bg-white border border-[#E2E8F0]" data-testid="cap-list">
          {filtered.length === 0 ? <div className="p-12 text-center text-sm text-[#94A3B8]"><ShieldAlert size={36} className="mx-auto mb-2 text-[#CBD5E1]" />No corrective actions found.</div> : (
            <div className="divide-y divide-[#F1F5F9]">
              {filtered.map(c => (
                <div key={c.id} className="px-4 py-3 flex items-center justify-between hover:bg-[#FAFAFA]" data-testid={`cap-${c.id}`}>
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => { setDetailCap(c); setCauseForm({ apparent_cause: c.apparent_cause || "", root_cause: c.root_cause || "", extent_of_condition: c.extent_of_condition || "" }); }}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-[#DC2626] font-bold">{c.cap_number}</span>
                      <DropdownBadge dropdownKey="cap_statuses" value={c.status} />
                      <DropdownBadge dropdownKey="cap_severities" value={c.severity} />
                    </div>
                    <p className="text-sm font-medium text-[#0F172A] mt-1">{c.title}</p>
                    <p className="text-[11px] text-[#64748B]">By {c.reported_by_name} · {new Date(c.created_at).toLocaleDateString()} · {(c.actions || []).length} actions</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {NEXT_STATUS[c.status] && <Button size="sm" onClick={() => advance(c.id, NEXT_STATUS[c.status])} className="rounded-none bg-[#2563EB] text-white text-[10px] font-bold" data-testid={`advance-${c.id}`}><ArrowRight size={11} className="mr-1" />{NEXT_STATUS[c.status].replace(/_/g, " ")}</Button>}
                    <Button size="sm" variant="outline" onClick={() => { setDetailCap(c); setCauseForm({ apparent_cause: c.apparent_cause || "", root_cause: c.root_cause || "", extent_of_condition: c.extent_of_condition || "" }); }} className="rounded-none text-[10px]"><Eye size={11} /></Button>
                    {isAdmin && <Button size="sm" variant="outline" onClick={() => deleteCAP(c.id)} className="rounded-none border-[#FECACA] text-[#DC2626] text-[10px]"><Trash2 size={11} /></Button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create CAP */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-md" data-testid="create-cap-dialog">
          <DialogHeader><DialogTitle>New Corrective Action</DialogTitle><DialogDescription className="text-xs text-[#64748B]">Report a condition requiring corrective action.</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-[10px] font-bold uppercase text-[#64748B]">Title *</Label><Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="mt-1 rounded-none text-sm" data-testid="cap-title-input" /></div>
            <div><Label className="text-[10px] font-bold uppercase text-[#64748B]">Description</Label><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="mt-1 w-full border border-[#E2E8F0] p-2 text-sm h-16 resize-none" data-testid="cap-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px] font-bold uppercase text-[#64748B]">Severity</Label><DropdownSelect dropdownKey="cap_severities" value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))} className="mt-1 w-full border border-[#E2E8F0] p-2 text-sm" testId="cap-severity" /></div>
              <div><Label className="text-[10px] font-bold uppercase text-[#64748B]">Source</Label><Input value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} placeholder="audit, NCR, etc." className="mt-1 rounded-none text-xs" data-testid="cap-source" /></div>
            </div>
            <div><Label className="text-[10px] font-bold uppercase text-[#64748B]">Affected Area</Label><Input value={form.affected_area} onChange={e => setForm(f => ({ ...f, affected_area: e.target.value }))} className="mt-1 rounded-none text-sm" data-testid="cap-area" /></div>
            <Button onClick={handleCreate} className="w-full bg-[#2563EB] text-white rounded-none text-xs font-bold" data-testid="cap-submit-btn">Create CAP Report</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* CAP Detail */}
      <Dialog open={!!detailCap} onOpenChange={(o) => { if (!o) setDetailCap(null); }}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="cap-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detailCap?.cap_number} — {detailCap?.title}</DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">Full CAP lifecycle: Report → Investigate → Plan → Implement → Verify → Close</DialogDescription>
          </DialogHeader>
          {detailCap && (
            <div className="space-y-4 mt-2">
              <div className="flex gap-2 flex-wrap"><DropdownBadge dropdownKey="cap_statuses" value={detailCap.status} /><DropdownBadge dropdownKey="cap_severities" value={detailCap.severity} />{detailCap.source && <span className="text-[10px] px-2 py-0.5 bg-[#F8FAFC] border border-[#E2E8F0] text-[#64748B]">Source: {detailCap.source}</span>}</div>
              {detailCap.description && <div><Label className="text-[10px] font-bold uppercase text-[#64748B]">Description</Label><p className="text-sm text-[#0F172A] mt-1">{detailCap.description}</p></div>}

              {/* Advance Button */}
              {NEXT_STATUS[detailCap.status] && (
                <Button size="sm" onClick={() => advance(detailCap.id, NEXT_STATUS[detailCap.status])} className="rounded-none bg-[#2563EB] text-white text-xs font-bold" data-testid="detail-advance-btn"><ArrowRight size={12} className="mr-1" />Advance to {NEXT_STATUS[detailCap.status].replace(/_/g, " ")}</Button>
              )}

              {/* Cause Analysis */}
              <fieldset className="border border-[#BFDBFE] p-3">
                <legend className="text-[10px] font-bold uppercase text-[#2563EB] px-1">Cause Analysis</legend>
                <div className="space-y-2">
                  <div><Label className="text-[10px] text-[#64748B]">Apparent Cause (ACE)</Label><textarea value={causeForm.apparent_cause} onChange={e => setCauseForm(f => ({ ...f, apparent_cause: e.target.value }))} className="mt-0.5 w-full border border-[#E2E8F0] p-2 text-xs h-12 resize-none" data-testid="cap-ace" /></div>
                  <div><Label className="text-[10px] text-[#64748B]">Root Cause (RCA)</Label><textarea value={causeForm.root_cause} onChange={e => setCauseForm(f => ({ ...f, root_cause: e.target.value }))} className="mt-0.5 w-full border border-[#E2E8F0] p-2 text-xs h-12 resize-none" data-testid="cap-rca" /></div>
                  <div><Label className="text-[10px] text-[#64748B]">Extent of Condition</Label><textarea value={causeForm.extent_of_condition} onChange={e => setCauseForm(f => ({ ...f, extent_of_condition: e.target.value }))} className="mt-0.5 w-full border border-[#E2E8F0] p-2 text-xs h-12 resize-none" data-testid="cap-extent" /></div>
                  <Button size="sm" onClick={saveCause} className="rounded-none bg-[#0F172A] text-white text-[10px]" data-testid="save-cause-btn">Save Cause Analysis</Button>
                </div>
              </fieldset>

              {/* Actions */}
              <fieldset className="border border-[#DDD6FE] p-3">
                <legend className="text-[10px] font-bold uppercase text-[#7C3AED] px-1">Corrective Actions ({(detailCap.actions || []).length})</legend>
                {(detailCap.actions || []).map(a => (
                  <div key={a.id} className="flex items-center gap-2 py-1.5 border-b border-[#F1F5F9] last:border-0 text-xs">
                    {a.status === "completed" ? <CheckCircle2 size={12} className="text-[#10B981]" /> : <Clock size={12} className="text-[#D97706]" />}
                    <span className={`flex-1 ${a.status === "completed" ? "line-through text-[#94A3B8]" : "text-[#0F172A]"}`}>{a.description}</span>
                    {a.assigned_to && <span className="text-[#64748B]">→ {a.assigned_to}</span>}
                    {a.due_date && <span className="text-[#D97706]">Due: {a.due_date}</span>}
                    {a.status !== "completed" && <Button size="sm" onClick={() => completeAction(a.id)} className="rounded-none bg-[#10B981] text-white text-[9px] h-5 px-2" data-testid={`complete-action-${a.id}`}>Done</Button>}
                  </div>
                ))}
                <div className="flex gap-2 mt-2">
                  <Input value={actionForm.description} onChange={e => setActionForm(f => ({ ...f, description: e.target.value }))} placeholder="Action description..." className="rounded-none text-[10px] h-7 flex-1" data-testid="action-desc" />
                  <Input value={actionForm.assigned_to} onChange={e => setActionForm(f => ({ ...f, assigned_to: e.target.value }))} placeholder="Assignee" className="rounded-none text-[10px] h-7 w-24" data-testid="action-assignee" />
                  <Input type="date" value={actionForm.due_date} onChange={e => setActionForm(f => ({ ...f, due_date: e.target.value }))} className="rounded-none text-[10px] h-7 w-28" data-testid="action-due" />
                  <Button size="sm" onClick={addAction} className="rounded-none bg-[#7C3AED] text-white text-[9px] h-7" data-testid="add-action-btn"><Plus size={10} /></Button>
                </div>
              </fieldset>

              {/* Effectiveness Review */}
              {detailCap.status === "verifying" && !detailCap.effectiveness_review && (
                <fieldset className="border border-[#BBF7D0] p-3">
                  <legend className="text-[10px] font-bold uppercase text-[#10B981] px-1">Effectiveness Review</legend>
                  <p className="text-xs text-[#64748B] mb-2">Were the corrective actions effective?</p>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => saveEffectiveness(true)} className="rounded-none bg-[#10B981] text-white text-xs font-bold" data-testid="effective-yes"><CheckCircle2 size={12} className="mr-1" />Effective</Button>
                    <Button size="sm" onClick={() => saveEffectiveness(false)} variant="outline" className="rounded-none border-[#DC2626] text-[#DC2626] text-xs font-bold" data-testid="effective-no"><AlertTriangle size={12} className="mr-1" />Not Effective</Button>
                  </div>
                </fieldset>
              )}
              {detailCap.effectiveness_review && (
                <div className={`p-3 border ${detailCap.effectiveness_review.effective ? "border-[#BBF7D0] bg-[#F0FDF4]" : "border-[#FECACA] bg-[#FEF2F2]"}`}>
                  <p className="text-xs font-bold">{detailCap.effectiveness_review.effective ? "Corrective actions EFFECTIVE" : "Corrective actions NOT EFFECTIVE"}</p>
                  <p className="text-[10px] text-[#64748B]">Reviewed by {detailCap.effectiveness_review.reviewed_by_name} · {new Date(detailCap.effectiveness_review.reviewed_at).toLocaleString()}</p>
                </div>
              )}

              {/* History */}
              {(detailCap.history || []).length > 0 && (
                <div>
                  <Label className="text-[10px] font-bold uppercase text-[#64748B]">History</Label>
                  <div className="space-y-1 mt-1">
                    {detailCap.history.map((h, i) => (
                      <div key={i} className="text-[10px] text-[#64748B]">{new Date(h.at).toLocaleString()} — <span className="text-[#0F172A] font-medium">{h.by}</span>: {h.action}{h.comment ? ` (${h.comment})` : ""}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
